import { CSSProperties, useEffect, useRef, useState } from "react";
import { GrDialog, GrDialogProps, GrEmpty, GrGridColDef, GrProgressCircle, GrStack, GrTable, GrTypography } from "@jnpmedi/grebob";
import { Pagination } from "@newComponents/Pagenation";
import { langHelper, MvnMessage } from "@newLibs/utils";
import { UseBoolean } from "@newHooks/common/useBoolean";
import { notify } from "@newHooks/context/snackbar/notify";
import { usePaginateDocsAuditMutation } from "@newHooks/mutation/docsAudit/paginateDocsAudit.mutation";
import { DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { FolderType } from "@maven-rest/docs/Folder.dto";
import { readableDocsAuditAction, readableDocsAuditResourceName, readableDocsAuditResourceType } from "@maven-rest/docs/RefAudit.conv";
import { DocsAudit, DocsAuditDataPayload } from "@maven-rest/docs/RefAudit.dto";
import { JsonHelper } from "@maven-helper/helpers/Json.helper";
import dayjs from "dayjs";

interface AuditTrailDialogProps {
  open: UseBoolean;
  organizationId: bigint;
  folderType: FolderType;
  folderId?: bigint;
  includeDeletedResource?: boolean;
  size?: GrDialogProps["size"];
}

export type TablePageType = { rpp: number; page: number; count: number };

const TABLE_HEIGHT: number = 548;

const loLang = langHelper.getLang();
export const DEFAULT_TABLE_PAGE_VALUE: TablePageType = {
  rpp: 10,
  page: 1,
  count: 0,
};

export function AuditTrailDialog({
  open,
  organizationId,
  folderType,
  folderId,
  includeDeletedResource = false,
  size = "medium",
}: AuditTrailDialogProps) {
  const [docsAudit, setDocsAudit] = useState<Map<number, DocsAudit[]>>(new Map());
  const [auditTrailPage, setAuditTrailPage] = useState<TablePageType>({ ...DEFAULT_TABLE_PAGE_VALUE });

  const prevRppRef = useRef<number>(10);

  useEffect(() => {
    auditTrailRequest({
      organizationId,
      folderType,
      folderId,
      includeDeletedResource,
      ...auditTrailPage,
    });
    return () => handleAuditTrailDialogClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const column: GrGridColDef<DocsAudit>[] = [
    {
      field: "Item",
      width: 165,
      renderHeader: () => renderTableHeader("Item"),
      renderCell: (item) => readableDocsAuditResourceType(item.row),
    },
    {
      field: "Resource",
      width: 165,
      renderHeader: () => renderTableHeader("Resource"),
      renderCell: (item) => {
        const audit = item.row;
        const parsedAfterPayload = JsonHelper.tryParse<DocsAuditDataPayload>(audit.after);

        if (!parsedAfterPayload) {
          throw new Error(`Unexpected Error, parsedAfterPayload not found (id: ${audit.id})`);
        }

        const resourceName = parsedAfterPayload.name || parsedAfterPayload.title || parsedAfterPayload.resource?.name || "";
        const readableResourceName = readableDocsAuditResourceName(audit, resourceName);
        const divStyle: CSSProperties = {
          overflow: "hidden",
          textOverflow: "ellipsis",
        };

        return (
          <div title={readableResourceName} style={divStyle}>
            {readableResourceName}
          </div>
        );
      },
    },
    {
      field: "Action",
      width: 165,
      renderHeader: () => renderTableHeader("Action"),
      renderCell: (item) => {
        const audit = item.row;
        const parsedAfterPayload = JsonHelper.tryParse<DocsAuditDataPayload>(audit.after);

        if (!parsedAfterPayload) {
          throw new Error(`Unexpected Error, parsedAfterPayload not found (id: ${audit.id})`);
        }

        const isDestroyed = parsedAfterPayload.isDestroyed;

        // TODO: 변경 내용의 상세 표시는 고도화 작업 때 진행 예정. 2023/09/11
        return readableDocsAuditAction(audit, loLang, isDestroyed);
      },
    },
    {
      field: "Actant",
      width: 165,
      renderHeader: () => renderTableHeader("Actant"),
      renderCell: (item) => item.row.actant.email,
    },

    {
      field: "Created At",
      width: 165,
      renderHeader: () => renderTableHeader("Created At"),
      valueGetter: (item) => dayjs(item.row.createdAt).format("YYYY/MM/DD HH:mm:ss Z"),
    },
  ];

  const { mutate: auditTrailRequest, isLoading: isAuditTrailLoading } = usePaginateDocsAuditMutation({
    query: {
      rpp: auditTrailPage.rpp.toString(),
      page: auditTrailPage.page.toString(),
    },
    onSuccess: (data) => {
      const { rpp, page, count, items } = data;

      if (rpp !== prevRppRef.current) {
        setDocsAudit(new Map([[1, items]]));
      } else {
        const copy = new Map(docsAudit);
        copy.set(page, items);
        setDocsAudit(copy);
      }

      setAuditTrailPage({ rpp, page, count: Number(count) });
    },
    onError: (error) => {
      notify(`Audit trail 조회에 실패하였습니다. [${error.message}]`, "WARN");
    },
  });

  const handleAuditTrailDialogClose = () => {
    open.onFalse();
    setDocsAudit(new Map());
    setAuditTrailPage({ ...DEFAULT_TABLE_PAGE_VALUE });
  };

  const handlePageChange = (newPage: { rpp: number; page: number; count: number }) => {
    const { rpp, page } = newPage;
    const isAuditTrailFetchNeed = (rpp: number, page: number): boolean => {
      return rpp !== auditTrailPage.rpp || (page !== auditTrailPage.page && !docsAudit.has(page));
    };

    if (isAuditTrailFetchNeed(rpp, page)) {
      prevRppRef.current = newPage.rpp;

      auditTrailRequest({
        organizationId,
        folderType,
        folderId,
        includeDeletedResource,
        ...newPage,
      });
    }
    setAuditTrailPage(newPage);
  };

  return (
    <GrDialog
      title={MvnMessage("Audit trail")}
      onClose={handleAuditTrailDialogClose}
      cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CLOSE_NOUN) }}
      open={open.value}
      size={size}
    >
      <GrStack flexDirection="column" height="fit-content">
        <GrTable
          headerData={column}
          rowSelection={false}
          disableAutosize
          cellData={docsAudit.get(auditTrailPage.page) || []}
          getRowId={(row) => String(row.id)}
          sx={{ height: TABLE_HEIGHT }}
          noRowsOverlay={
            <GrStack justifyContent="center" alignItems="center" height={"100%"}>
              <GrEmpty type="simple" title={"No item"} />
            </GrStack>
          }
          loading={isAuditTrailLoading}
          loadingOverlay={
            <GrStack width="100%" height={TABLE_HEIGHT} alignItems="center" justifyContent="center">
              <GrProgressCircle size={40} value={80} variant="indeterminate" />
            </GrStack>
          }
        />
        <Pagination
          {...auditTrailPage}
          onChangePage={(page) => handlePageChange({ ...auditTrailPage, page })}
          onChangeRowsPerPage={(rpp) => handlePageChange({ ...auditTrailPage, rpp, page: 1 })}
        />
      </GrStack>
    </GrDialog>
  );
}

function renderTableHeader(headerName: string) {
  return (
    <GrTypography type="Content.Subtitle04" color="grey090">
      {headerName}
    </GrTypography>
  );
}
